import React, { useState } from 'react';
import styled from 'styled-components';
import { SharedLayoutUltimate } from '../components/layout/SharedLayoutUltimate';
import { theme } from '../styles/theme';
import { useCalendlyPopup } from '../hooks/useCalendlyPopup';

const HeroSection = styled.section`
  background: linear-gradient(135deg, #232B3B 0%, #1a1f2e 100%);
  padding: 100px 20px 40px;
  margin-top: -80px;
  padding-top: 140px;
  text-align: center;
  color: white;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const PageTitle = styled.h1`
  font-size: clamp(36px, 5vw, 56px);
  font-weight: 800;
  margin-bottom: 16px;
  letter-spacing: -1px;
`;

const PageSubtitle = styled.h2`
  font-size: clamp(20px, 2.5vw, 24px);
  font-weight: 400;
  margin-bottom: 30px;
  opacity: 0.9;
`;

const ContentWrapper = styled.div`
  display: flex;
  max-width: 1400px;
  margin: 0 auto;
  gap: 40px;
  padding: 0 20px;

  @media (max-width: ${theme.breakpoints.tablet}) {
    flex-direction: column;
    gap: 20px;
  }
`;

const TableOfContents = styled.div`
  flex: 0 0 350px;
  background: #f8f9fa;
  border-radius: 12px;
  padding: 30px;
  height: fit-content;
  position: sticky;
  top: 100px;
  margin-top: -40px;

  @media (max-width: ${theme.breakpoints.tablet}) {
    position: static;
    margin-top: 20px;
    flex: 1;
  }
`;

const TOCTitle = styled.h3`
  font-size: 18px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 20px;
  color: #333;
`;

const TOCItem = styled.div<{ isActive?: boolean }>`
  padding: 12px 15px;
  margin-bottom: 8px;
  background: ${props => props.isActive ? '#232B3B' : 'white'};
  color: ${props => props.isActive ? 'white' : '#333'};
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);

  &:hover {
    background: ${props => props.isActive ? '#232B3B' : '#e9ecef'};
    transform: translateX(5px);
  }
`;

const TOCItemTitle = styled.span`
  font-weight: 500;
  font-size: 14px;
  flex: 1;
`;

const TOCItemTime = styled.span`
  font-size: 12px;
  opacity: 0.7;
  margin-left: 8px;
`;

const MainContent = styled.main`
  flex: 1;
  padding: 40px 0;
`;

const ContentSection = styled.section`
  background: white;
  border-radius: 12px;
  padding: 40px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  margin-bottom: 30px;
`;

const SectionTitle = styled.h2`
  font-size: 32px;
  font-weight: bold;
  margin-bottom: 30px;
  color: #232B3B;
  padding-bottom: 15px;
  border-bottom: 3px solid #ffd700;
`;

const SectionContent = styled.div`
  font-size: 18px;
  line-height: 1.8;
  color: #444;

  p {
    margin-bottom: 20px;
  }

  ul, ol {
    margin: 20px 0;
    padding-left: 30px;
  }

  li {
    margin-bottom: 12px;
  }

  strong {
    color: #232B3B;
    font-weight: 600;
  }

  h3 {
    font-size: 24px;
    font-weight: 600;
    color: #232B3B;
    margin: 30px 0 20px 0;
  }

  em {
    font-style: italic;
  }

  a {
    color: #ffd700;
    text-decoration: none;
    font-weight: 500;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const CallToAction = styled.div`
  background: #232B3B;
  color: white;
  text-align: center;
  padding: 60px 20px;
  border-radius: 12px;
  margin-top: 40px;
`;

const CTATitle = styled.h3`
  font-size: 36px;
  font-weight: bold;
  margin-bottom: 20px;
`;

const CTAText = styled.p`
  font-size: 20px;
  margin-bottom: 30px;
  opacity: 0.9;
`;

const CTAButton = styled.button`
  background: #ffd700;
  color: #232B3B;
  padding: 18px 40px;
  font-size: 18px;
  font-weight: bold;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: #ffed4a;
    transform: translateY(-2px);
    box-shadow: 0 10px 20px rgba(255, 215, 0, 0.3);
  }
`;

const NavigationButtons = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 40px;
  gap: 20px;
`;

const NavButton = styled.button<{ direction: 'prev' | 'next' }>`
  background: ${props => props.direction === 'next' ? '#ffd700' : '#e9ecef'};
  color: ${props => props.direction === 'next' ? '#232B3B' : '#333'};
  padding: 15px 30px;
  font-size: 16px;
  font-weight: 600;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 10px;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

type SectionId = 'what-is-annuity' | 'types-of-annuities' | 'how-annuities-work' | 
  'who-should-consider' | 'whats-next';

export const AnnuityBasics: React.FC = () => {
  const [activeSection, setActiveSection] = useState<SectionId>('what-is-annuity');
  const { openCalendly } = useCalendlyPopup();

  const sections: Array<{ id: SectionId; title: string; time: string }> = [
    { id: 'what-is-annuity', title: 'What Is An Annuity?', time: '1 min' },
    { id: 'types-of-annuities', title: 'Types of Annuities', time: '4 mins' },
    { id: 'how-annuities-work', title: 'How Annuities Work and The Story Behind Them', time: '8 mins' },
    { id: 'who-should-consider', title: 'Who Should Consider an Annuity?', time: '2 mins' },
    { id: 'whats-next', title: "What's Next", time: '4 mins' }
  ];

  const sectionContent: Record<SectionId, { title: string; content: string }> = {
    'what-is-annuity': {
      title: 'What Is An Annuity?',
      content: `
        <p>An annuity is a product that provides a stream of income payments. But did you know that the word annuity is actually multi-faceted? It can refer to a variety of products and income streams, each with its own unique features and benefits.</p>
        
        <p>For example, some annuity products guarantee that you won't lose a penny when the market or indices fall, and have no internal fees. Others are directly invested in securities and carry market risk and applicable fees. There are also actuarial income streams, which are something completely different.</p>
        
        <p>However, when people say the word, "annuity," today, they are usually referring to insurance products that offer a stream of income payments through a contractual agreement with an insurance carrier. These annuity insurance contracts specify all interest calculation methods, ensuring that you know exactly what you're getting.</p>
        
        <p>So, while the word, "annuity," can mean many different things, over 90% of the time people are referring to annuity insurance contracts, not simply an actuarial income stream. We hope this helps clarify what an annuity is and what it can do for you!</p>
      `
    },
    'types-of-annuities': {
      title: 'Types of Annuities',
      content: `
        <p>An annuity is an insurance product designed to help you save money for retirement. There are different types of annuities, and you should know what they are so you can choose the one that's right for you.</p>
        
        <p>The two main types of annuities are variable and fixed.</p>
        
        <p>In a variable annuity, the value of your annuity account can go up or down depending on a related market index or other assets depending upon the annuity.</p>
        
        <p>In a fixed annuity, the value of your principal deposit remains the same, regardless of market index losses. Fixed annuities can be further broken down into traditional fixed and fixed indexed annuities.</p>
        
        <p>If you are new to annuities and you are looking for both security of principal and performance, I would share that the Averill family has had great success purchasing specific fixed annuities.</p>
        
        <p>Structured annuities are a newer type of annuity that are designed to balance risk and return. They offer some exposure to principal loss, but within a specified parameter to limit risk.</p>
        
        <p>In addition to these basic types, there are different subsets of products that are created for different purposes. For example, an income rider contract is a type of fixed indexed annuity product that provides regular income payments, while a multiple year rate guarantee annuity (MYGA) provides a higher interest rate for a specific period of time.</p>
        
        <p>When choosing an annuity, you'll want to consider factors like surrender charges, beneficiary options, and fees. The Averill family portfolio uses some multiple year rate guarantee contracts that provide guaranteed fixed interest rates throughout the surrender charge period. In addition, we only consider contracts that leave any remaining amounts to be paid out upon death to the beneficiaries with no surrender charge.</p>
        
        <p>It's also important to understand the terms of the contract, such as the guaranteed minimum annuitization calculation balance, and to properly manage the contract after purchase to avoid losing money. If you're new to annuities and looking for security and performance, we can help recommend the right one to fit your specific needs.</p>
        
        <p>Cap Averill shared his perspective; He said he doesn't gamble against the house, even if the house can't stack the deck against him. He'd only consider buying an annuity if all the parameters were locked down and the company couldn't control its performance after the purchase.</p>
        
        <p>In our publication entitled, "Only One Annuity," we answer the question of what type of annuity we would choose if we were stranded on a desert island and could only buy one subtype. We believe that there is really only one type of annuity we would buy: a stripped-down, no-frills, aggressive fixed indexed annuity that tracks the Standard & Poor's 500. We also only consider annuities from highly rated companies that have historically honored their rates after the point of sale.</p>
        
        <p>We don't pay any fees unless our historical testing shows that it's not only paid for itself but is also profitable at extremely high propensities. Cap Averill always said that he wouldn't pay an insurance company an extra percent for an additional guarantee in case the crediting methods they control underperform. "It's against my survival instincts," he would say. On the other hand, we have fixed indexed annuities that pay 100% of the growth of the Standard & Poor's 500 based on a nominal fee.</p>
      `
    },
    'how-annuities-work': {
      title: 'How Annuities Work and The Story Behind Them',
      content: `
        <h3>Early Annuities: A Simple Start</h3>
        
        <p>In the early 1980s, annuities were extremely simple. Typically, a company would purchase rated securities such as T-bills, or the like, at the respective interest rate at the time, let's say 5% for example, although it was much higher back then.</p>
        
        <p>They would determine how much it would cost them to issue the contracts, the benefits of the contracts, market the contract, and then how much profit they wanted to make. Let's say all of that total is 1/2 of 1% per year.</p>
        
        <p>The company would then offer an interest rate of 4½% to the public and sell as many as they could. That was considered the traditional fixed annuity.</p>
        
        <p>At that time, you could generally assume that the current offered rate of the company would remain the same throughout the contract surrender, even though it was not guaranteed to do so. If you needed it to be guaranteed, multiple year guaranteed annuities or MYGAs were available.</p>
        
        <p>Then, something interesting happened.</p>
        
        <h3>Changing Landscape: The Fixed Indexed Annuity Emerges</h3>
        
        <p>In the late 1980s, a company called Keystone pioneered something called the fixed indexed annuity. This seemed very suspect to me. It claimed to pay upside potential of the stock market but not to lose money when the market fell.</p>
        
        <p>I remember my father joking about a local farmer who was being paid not to farm land by a program. He called my father and asked him how that was working out. He said that they did so well not farming 100 acres the first year, that they tried not farming 200 acres the second year. But it was a case of growing too big too fast, and they lost their shirts. Then he cracked a smile, indicating that it was all a joke.</p>
        
        <p>I kept thinking that upside potential of the stock market without downside sounded fishy. So, I actually visited with the pioneers personally, people at Lincoln, National, and Keystone. I explained my father's company philosophy of never recommending anything that we did not purchase ourselves. At that time, you could make 10% of the stock market upside, but if the market fell, you would lose nothing.</p>
        
        <p>I requested the sales marketing manager sign an informal agreement stating that there was no stock market risk in the contract and sheepishly deposited $10,000. It did return 10% for the majority of the five years I was in the contract, to my surprise. But what was more interesting was that when the market fell, I lost nothing. I was a believer.</p>
        
        <p>What happened? How did they do that? Here's how.</p>
        
        <p>It was a twist on the traditional fixed annuity. Quite simply, they took the same interest rate the client was to receive in the first place and gave the client an option of allowing the company to buy a call option on a market index with that same money. Brilliant.</p>
        
        <p>You don't need to know a lot about options to understand that they either function or not. This made perfect sense to me. That's how, if the markets go up, the insurance company has an option to exercise which provides the return on the annuity. The insurance company is being reimbursed by the options market. They also explained to me how their annuity does not lose money when the market falls because the money was never in the market. The only amount placed at risk by the insurance company was the interest rate the annuity was going to receive in that year. So, after these discussions I clearly understood how a fixed indexed annuity could have the upside potential associated with the market, without all of the downside market risk.</p>
        
        <p>Many variations of annuities have come and gone over the years. Structured annuities and variable annuities are attempts to limit market risks by utilizing certain contractual provisions. However, our family does not believe in taking risks and making them safer through contractual provisions. This does not mean that we are against risk. We simply have risk money in equities managed by fiduciaries who credit annuity commissions earned against our fiduciary asset management fees. As we do not pay these annuity commissions out-of-pocket, our risk management by the fiduciary management team typically has low or no cost. They also handle our annuity recommendations using our scientific tools. It makes perfect sense to us to take a certain portion of your money and try to increase returns, which can reasonably be expected from it.</p>
        
        <p>Historical backtesting on any calculation method is possible and can be used.</p>
        
        <p>Structured annuities may limit some market risk, but they are still risk vehicles. The same is true for variable annuities. Although we do not play in that environment at all with Averill money in the Averill family portfolio, we can still assist you and help guide you through the process if you choose to purchase these types of annuity products.</p>
        
        <p>At Cap Averill II & Associates, we did not see the upside potential as being available to us at lower cost, or with a higher propensity, or lower risk. But we know that the vast majority of people who seek annuities are seeking stability of some sort, and we are also among those people.</p>
        
        <p>The fixed index annuity has gone through several incarnations. New crediting or allocation methods have been developed over the past few decades, all of which have been tried, tested, and either accepted or rejected by our agents and equipment. Each of them has their own thrills of victory and agony of defeat.</p>
        
        <p>It was only in 2014, almost 30 years after the inception of the product, that we advocated for certain types of allocations that would put us and our management team in the position of literally eviscerating the Date Lottery issue, which has plagued owners of fixed indexed annuities from the outset. Our management style of the right index options is proprietary and will be shared only with our clients.</p>
        
        <p>The current environment and propensities for available allocation methods to perform more efficiently and at higher levels with very low levels of possible disappointment, if managed properly, have never been better than they are now, in our view. This has not always been the case. We found 2012 and 2020 during the coronavirus to be typically horrible times to purchase annuities because the rates on the products purchased by insurance companies to back their annuities were so low.</p>
        
        <p>Candidly, there's not much to discuss in a fixed annuity. It is essentially the interest rate the insurance company can get at the time, minus their spread of how much it costs them to put the product on the books, plus their desired profit. This ultimately equals the amount they can pay the client. The rest of the specifics are included in the contract.</p>
      `
    },
    'who-should-consider': {
      title: 'Who Should Consider an Annuity?',
      content: `
        <p>Who might want to consider an annuity? The answer may surprise you!</p>
        
        <p>While it's often assumed that only those seeking retirement income should consider annuities, we believe that this is a misguided stereotype. We don't blindly accept any stereotype without further consideration. For example, advisors may say that younger people should not use annuities for after-tax money because of the 10% penalty charged when withdrawing deferred interest from an after-tax annuity. While that may seem like a logical reason not to buy annuities, there are situations where annuities can be beneficial for younger individuals. For instance, if a younger couple has a portion of their assets left to them by their parents for the benefit of their children, they may not have any interest in withdrawing any interest in the near future. In this case, a tax penalty can be avoided by simply not taking withdrawals until the penalty no longer applies. Therefore, there's no reason not to consider accumulation annuities for this subset of a younger couple's money, especially considering no tax is charged against growth during the couple's high earning years.</p>
        
        <p>Another common misconception is that annuities are only suitable for qualified money like IRA-type monies. However, this is not the case. It doesn't matter what you have your IRA invested in; you'll still pay a penalty for early withdrawal to the IRS and regular income tax, which is not higher or lower in tax-deferred annuities. So, logically, there's no reason why younger people, depending upon their financial situation, wouldn't consider accumulation annuities for protection of principal and growth on their qualified monies as well.</p>
        
        <p>One of the biggest reasons we believe younger people do not more aggressively use these products is misinformation about negative tax consequences and the lack of general knowledge about the higher level of performance at high propensity in accumulation annuities. Additionally, we're constantly barraged with information suggesting that the stock market is "where it's at," despite the fact that there have been five major economic surprises/downturns during the last few decades that have eviscerated monies in markets, causing several years of downtime waiting for values to return.</p>
        
        <p>Comparing that against something that does not lose principal when market indices fall but has no limit on the percentage of the gain of the market credit when markets gain, it's clear that annuities are a fair fight at best and in the worst of scenarios, annuities would likely do extremely better. In the best of scenarios, annuities may not do quite as well, which suggests that a balance of products be used to provide both the best of upside potential while reducing the potential of being wiped out by an unexpected negative market sequence.</p>
        
        <p>Annuities can be a good option for long-term savings, retirement, or inheritance purposes, especially for tax-qualified funds like 401(k) or IRA. Annuities provide tax-deferred growth, which means that you don't pay taxes on your earnings until you withdraw the money. However, premature withdrawal from non-qualified funds may result in a 10% penalty. Depending on the interest rate, the client's tax rate, and how long the money is left to defer, annuities can provide higher returns than CDs even after paying the penalty and taxes. Cap Averill purchased deferred annuities for his grandchildren when they were young, and those same annuities were not activated for school because the kids wound up getting scholarships and paying their way through. So, the money continued in deferral in their names. It's amazing to see how quickly money grows when we are not charged any income tax on the earnings, but instead we are only charged income tax on the money we decide to withdraw. That is one of the benefits we get from tax-deferred annuities.</p>
        
        <h3>Age Breakdown</h3>
        
        <p>Are you dreaming of retiring early, but worried about being hit with penalties for withdrawing from your 401(k) before age 59 1/2? Don't let that stop you! While most people might tell you it's not possible, many agents are not familiar with the 72T concept that can help you retire early. If you're 55 and fully retired, there's a way to structure your income using an annuity that's not subject to the 10% penalty. This annuity pays out partially as a return of principal and partially as interest, at a tax-preferred rate. This means you can retire without paying income tax penalties on your IRAs, 401(k)s, or other tax-qualified money.</p>
        
        <p>While this option may not be well-known or recommended by some, it's worth exploring if you're considering early retirement. So go ahead, retire on your terms and enjoy the freedom you've worked so hard to achieve! For those aged 59½ or above, any withdrawals made from qualified annuities or after-tax annuities will not incur a 10% penalty. Instead, you will only pay regular income tax on the interest you've earned during your lifetime, and only on the amount you withdraw. This means that with some planning, we can spread the tax burden over multiple tax years and reduce our overall tax liability. If we are under 75 years of age, we may be in the best position to purchase fixed, indexed, and other annuities. It is important to note that insurance companies limit the maximum age of their available products due to the uncertainty of how many additional years an individual may live. As such, if we are 75 years of age or less, we will likely have a wider range of product options to choose from.</p>
        
        <p>For those between 75 and 80 years of age, we will likely have a shorter list of products available to us, and the longer surrender charge periods may be less available or not available at all. However, there are still several ten-year annuity products with high accumulation attributes, and a number of five and seven-year annuity products that may be appropriate for our specific financial situation to choose from.</p>
        
        <p>For those older than 80 but not yet 85, extreme caution must be taken in purchasing annuities since not all products are designed for significant growth, and some may come with additional charges. It is best to seek the advice of our other financial professionals, including our CPA, tax advisors, and other fiduciaries to ensure that we fully understand the costs and terms of the product we are considering and how the product relates to our specific financial situation.</p>
        
        <p>Finally, for those above 90 years of age, it may be extremely challenging to find viable accumulation annuities given the uncertainty of lifespan at that age.</p>
        
        <p><em>*Please note we do not provide tax advice; you should consult your accountants and tax professionals for tax advice.</em></p>
      `
    },
    'whats-next': {
      title: "What's Next",
      content: `
        <p>Thank you for reading our Annuity Basics articles. These articles were written to help outline the surface-level basics of annuities to anyone looking into retirement or annuity investments in general.</p>
        
        <p>We have much more detailed content on this website too; we cover the progression and development of allocation methods or interest calculation methods, which have been designed in annuities since the 1980s, up to and including the ones that we favor. We will also provide statistics that prove out the vulnerabilities of most of the most popular currently available contract allocation methods.</p>
        
        <p>If you're serious about getting the best annuity for yourself, remember that you'll indirectly pay a commission to someone since the company will compensate your agent (including our agents) upon the purchase.</p>
        
        <p>We are here to help you through the process, if you wish to open that door. However, keep in mind that although we do not directly bill you for our services, we do earn a profit by being directly paid by the insurance companies that our clients select.</p>
        
        <p><a href="/how-we-get-paid">Click here to learn how we get paid</a></p>
        
        <p>Because of the streamlined nature of our business, the highly efficient systems we have created, and the number of clients we service at a high level, we are able to be profitable even selling the lowest commission products available in the marketplace, if those products are what our fiduciaries believe to be the best fit for the client's financial situation.</p>
        
        <p>Do not hesitate to ask us to walk you through the search.</p>
        
        <p><a href="/contact" data-open-calendly>Click here to book a 15 minute meeting with one of our experienced advisors.</a></p>
        
        <p>Ask us directly which contract we would choose if we were you.</p>
        
        <p>You are not obligated to purchase anything from us, for providing you with our honest and open feedback.</p>
      `
    }
  };

  const handleSectionClick = (sectionId: SectionId) => {
    setActiveSection(sectionId);
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const getCurrentSectionIndex = () => sections.findIndex(s => s.id === activeSection);

  const handleNavigation = (direction: 'prev' | 'next') => {
    const currentIndex = getCurrentSectionIndex();
    if (direction === 'prev' && currentIndex > 0) {
      handleSectionClick(sections[currentIndex - 1].id);
    } else if (direction === 'next' && currentIndex < sections.length - 1) {
      handleSectionClick(sections[currentIndex + 1].id);
    }
  };

  return (
    <SharedLayoutUltimate>
      <HeroSection>
        <Container>
          <PageTitle>Annuity Basics</PageTitle>
          <PageSubtitle>Everything you need to know about annuities in {new Date().getFullYear()}</PageSubtitle>
        </Container>
      </HeroSection>

      <ContentWrapper>
        <TableOfContents>
          <TOCTitle>Contents</TOCTitle>
          {sections.map((section) => (
            <TOCItem
              key={section.id}
              isActive={activeSection === section.id}
              onClick={() => handleSectionClick(section.id)}
            >
              <TOCItemTitle>{section.title}</TOCItemTitle>
              <TOCItemTime>{section.time}</TOCItemTime>
            </TOCItem>
          ))}
        </TableOfContents>

        <MainContent>
          {sections.map((section) => {
            const content = sectionContent[section.id];
            return (
              <ContentSection key={section.id} id={section.id}>
                <SectionTitle>{content.title}</SectionTitle>
                <SectionContent 
                  dangerouslySetInnerHTML={{ __html: content.content }}
                  onClick={(e) => {
                    // Links inside the HTML strings can't carry React handlers, so delegate here
                    if ((e.target as HTMLElement).closest('[data-open-calendly]')) {
                      e.preventDefault();
                      openCalendly();
                    }
                  }}
                />
                <NavigationButtons>
                  <NavButton
                    direction="prev"
                    onClick={() => handleNavigation('prev')}
                    disabled={getCurrentSectionIndex() === 0}
                  >
                    ← Previous
                  </NavButton>
                  <NavButton
                    direction="next"
                    onClick={() => handleNavigation('next')}
                    disabled={getCurrentSectionIndex() === sections.length - 1}
                  >
                    Next →
                  </NavButton>
                </NavigationButtons>
              </ContentSection>
            );
          })}

          <CallToAction>
            <CTATitle>Ready to Find Your Perfect Annuity?</CTATitle>
            <CTAText>
              Let our experts guide you through the process with our AI-driven approach<br />
              No cost, no obligation consultation
            </CTAText>
            <CTAButton onClick={() => window.location.href = '/retirevana'}>
              SCHEDULE YOUR FREE CONSULTATION
            </CTAButton>
          </CallToAction>
        </MainContent>
      </ContentWrapper>
    </SharedLayoutUltimate>
  );
};